import { useEffect, useRef, useCallback } from 'react';
import { Terminal as XTerm } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import { WebLinksAddon } from '@xterm/addon-web-links';
import { Maximize2, Minimize2, Trash2, Download } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { cn } from '@/utils/cn';
import '@xterm/xterm/css/xterm.css';

interface TerminalPanelProps {
  projectSlug: string;
  onCommand?: (command: string) => void;
  className?: string;
}

export function TerminalPanel({ projectSlug, onCommand, className }: TerminalPanelProps) {
  const terminalRef = useRef<HTMLDivElement>(null);
  const xtermRef = useRef<XTerm | null>(null);
  const fitAddonRef = useRef<FitAddon | null>(null);
  const isFullscreenRef = useRef(false);

  const initTerminal = useCallback(() => {
    if (!terminalRef.current || xtermRef.current) return;

    const xterm = new XTerm({
      cursorBlink: true,
      cursorStyle: 'bar',
      fontSize: 13,
      fontFamily: 'JetBrains Mono, Menlo, Monaco, monospace',
      theme: {
        background: '#1a1a2e',
        foreground: '#e4e4e7',
        cursor: '#e4e4e7',
        cursorAccent: '#1a1a2e',
        selectionBackground: '#3730a3',
        black: '#1a1a2e',
        red: '#ef4444',
        green: '#22c55e',
        yellow: '#eab308',
        blue: '#3b82f6',
        magenta: '#a855f7',
        cyan: '#06b6d4',
        white: '#e4e4e7',
        brightBlack: '#6b7280',
        brightRed: '#f87171',
        brightGreen: '#4ade80',
        brightYellow: '#facc15',
        brightBlue: '#60a5fa',
        brightMagenta: '#c084fc',
        brightCyan: '#22d3ee',
        brightWhite: '#ffffff',
      },
      scrollback: 1000,
      convertEol: true,
    });

    const fitAddon = new FitAddon();
    const webLinksAddon = new WebLinksAddon();

    xterm.loadAddon(fitAddon);
    xterm.loadAddon(webLinksAddon);

    xterm.open(terminalRef.current);
    fitAddon.fit();

    // Store refs
    xtermRef.current = xterm;
    fitAddonRef.current = fitAddon;

    // Write welcome message
    xterm.writeln('\x1b[1;36m╔══════════════════════════════════════════╗\x1b[0m');
    xterm.writeln('\x1b[1;36m║\x1b[0m        \x1b[1;33mSpark Terminal\x1b[0m                   \x1b[1;36m║\x1b[0m');
    xterm.writeln('\x1b[1;36m╚══════════════════════════════════════════╝\x1b[0m');
    xterm.writeln('');
    xterm.writeln(`Connected to project: \x1b[1;32m${projectSlug}\x1b[0m`);
    xterm.writeln('Type \x1b[1mhelp\x1b[0m for available commands.');
    xterm.writeln('');
    writePrompt(xterm);

    // Handle input
    let currentLine = '';

    xterm.onData((data) => {
      switch (data) {
        case '\r': // Enter
          xterm.write('\r\n');
          if (currentLine.trim()) {
            handleCommand(xterm, currentLine.trim());
            onCommand?.(currentLine.trim());
          }
          currentLine = '';
          writePrompt(xterm);
          break;
        case '\x7f': // Backspace
          if (currentLine.length > 0) {
            currentLine = currentLine.slice(0, -1);
            xterm.write('\b \b');
          }
          break;
        case '\x03': // Ctrl+C
          xterm.write('^C\r\n');
          currentLine = '';
          writePrompt(xterm);
          break;
        default:
          if (data >= ' ' || data === '\t') {
            currentLine += data;
            xterm.write(data);
          }
      }
    });

    // Handle resize
    const handleResize = () => {
      fitAddon.fit();
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      xterm.dispose();
    };
  }, [projectSlug, onCommand]);

  useEffect(() => {
    const cleanup = initTerminal();
    return () => {
      cleanup?.();
      xtermRef.current = null;
      fitAddonRef.current = null;
    };
  }, [initTerminal]);

  const handleClear = () => {
    if (xtermRef.current) {
      xtermRef.current.clear();
      writePrompt(xtermRef.current);
    }
  };

  const handleExport = () => {
    // Export terminal content
    if (xtermRef.current) {
      const content = xtermRef.current.buffer.active;
      let text = '';
      for (let i = 0; i < content.length; i++) {
        const line = content.getLine(i);
        if (line) {
          text += line.translateToString() + '\n';
        }
      }

      const blob = new Blob([text], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `terminal-${projectSlug}-${Date.now()}.txt`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  const toggleFullscreen = () => {
    isFullscreenRef.current = !isFullscreenRef.current;
    // Force re-fit after layout change
    setTimeout(() => fitAddonRef.current?.fit(), 100);
  };

  return (
    <div className={cn('flex flex-col h-full bg-[#1a1a2e]', className)}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 bg-[#141425] border-b border-gray-800">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-full bg-red-500" />
            <div className="w-3 h-3 rounded-full bg-yellow-500" />
            <div className="w-3 h-3 rounded-full bg-green-500" />
          </div>
          <span className="ml-2 text-sm text-gray-400">Terminal</span>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClear}
            className="text-gray-400 hover:text-white"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleExport}
            className="text-gray-400 hover:text-white"
          >
            <Download className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleFullscreen}
            className="text-gray-400 hover:text-white"
          >
            <Maximize2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Terminal */}
      <div ref={terminalRef} className="flex-1 p-2" />
    </div>
  );
}

function writePrompt(xterm: XTerm) {
  xterm.write('\x1b[1;32m➜\x1b[0m \x1b[1;34m~/project\x1b[0m $ ');
}

function handleCommand(xterm: XTerm, command: string) {
  const [cmd, ...args] = command.split(' ');

  switch (cmd.toLowerCase()) {
    case 'help':
      xterm.writeln('\x1b[1mAvailable Commands:\x1b[0m');
      xterm.writeln('  \x1b[33mhelp\x1b[0m      - Show this help message');
      xterm.writeln('  \x1b[33mls\x1b[0m        - List files in current directory');
      xterm.writeln('  \x1b[33mcat\x1b[0m       - Display file contents');
      xterm.writeln('  \x1b[33mpwd\x1b[0m       - Print working directory');
      xterm.writeln('  \x1b[33mclear\x1b[0m     - Clear terminal');
      xterm.writeln('  \x1b[33mnpm\x1b[0m       - Run npm commands');
      xterm.writeln('  \x1b[33mgit\x1b[0m       - Run git commands');
      break;
    case 'ls':
      xterm.writeln('\x1b[34mnode_modules/\x1b[0m  \x1b[34msrc/\x1b[0m  \x1b[34mpublic/\x1b[0m');
      xterm.writeln('package.json   tsconfig.json  vite.config.ts');
      xterm.writeln('README.md      .env           .gitignore');
      break;
    case 'pwd':
      xterm.writeln('/home/spark/project');
      break;
    case 'clear':
      xterm.clear();
      break;
    case 'npm':
      if (args[0] === 'run' && args[1] === 'dev') {
        xterm.writeln('\x1b[36m> vite\x1b[0m');
        xterm.writeln('');
        xterm.writeln('  \x1b[32mVITE v5.0.0\x1b[0m  ready in \x1b[33m312\x1b[0m ms');
        xterm.writeln('');
        xterm.writeln('  ➜  \x1b[1mLocal\x1b[0m:   \x1b[36mhttp://localhost:5173/\x1b[0m');
        xterm.writeln('  ➜  \x1b[1mNetwork\x1b[0m: \x1b[36mhttp://192.168.1.100:5173/\x1b[0m');
      } else {
        xterm.writeln(`npm ${args.join(' ')}`);
      }
      break;
    case 'git':
      if (args[0] === 'status') {
        xterm.writeln('On branch \x1b[36mmain\x1b[0m');
        xterm.writeln('Your branch is up to date with \'origin/main\'.');
        xterm.writeln('');
        xterm.writeln('nothing to commit, working tree clean');
      } else {
        xterm.writeln(`git ${args.join(' ')}`);
      }
      break;
    default:
      xterm.writeln(`\x1b[31mCommand not found: ${cmd}\x1b[0m`);
      xterm.writeln('Type \x1b[1mhelp\x1b[0m for available commands.');
  }
}
